import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { AlertCircle, CheckCircle2, Info, X as CloseIcon } from 'lucide-react';
import { Z } from '../constants/zIndex';
import Button from '../components/ui/Button';

const ToastContext = createContext(null);

const MAX_TOASTS = 3;
const DEFAULT_DURATION_MS = 3500;
const ERROR_DURATION_MS = 6000;

const AUTH_ROUTES = new Set([
  '/',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/reset-success',
  '/check-email',
  '/verify-email',
  '/verify-2fa',
  '/accept-invite',
  '/auth-callback'
]);

const shouldSuppressToasts = (pathname) => {
  if (pathname.startsWith('/onboarding')) return true;
  if (AUTH_ROUTES.has(pathname)) return true;
  return false;
};

const sanitizeHeadline = (headline = '') => String(headline).replace(/^[✅❌⚠️ℹ️]+\s*/u, '').trim();

const getToastTone = (type) => {
  switch (type) {
    case 'success':
      return {
        Icon: CheckCircle2,
        iconClassName: 'text-emerald-400',
        iconShellClassName: 'border-emerald-400/20 bg-emerald-400/10',
      };
    case 'error':
      return {
        Icon: AlertCircle,
        iconClassName: 'text-rose-400',
        iconShellClassName: 'border-rose-400/20 bg-rose-400/10',
      };
    default:
      return {
        Icon: Info,
        iconClassName: 'text-sky-400',
        iconShellClassName: 'border-sky-400/20 bg-sky-400/10',
      };
  }
};

const ToastItem = ({ toast, onDismiss }) => {
  const timerRef = useRef(null);
  const startRef = useRef(Date.now());
  const remainingRef = useRef(toast.durationMs);
  const actionFiredRef = useRef(false);
  const tone = getToastTone(toast.type);
  const { Icon } = tone;
  const hasSubheadline = Boolean(toast.subheadline);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    startRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      onDismiss(toast.id);
    }, remainingRef.current);
  }, [clearTimer, onDismiss, toast.id]);

  const handleMouseEnter = useCallback(() => {
    if (!timerRef.current) return;
    clearTimer();
    const elapsed = Date.now() - startRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
  }, [clearTimer]);

  const handleMouseLeave = useCallback(() => {
    if (remainingRef.current <= 0) {
      onDismiss(toast.id);
      return;
    }
    startTimer();
  }, [onDismiss, startTimer, toast.id]);

  React.useEffect(() => {
    startTimer();
    return clearTimer;
  }, [clearTimer, startTimer]);

  const handleAction = useCallback(() => {
    if (actionFiredRef.current || !toast.action?.onClick) return;
    actionFiredRef.current = true;
    toast.action.onClick();
    onDismiss(toast.id);
  }, [toast.action, toast.id, onDismiss]);

  return (
    <div
      className="w-full rounded-2xl border border-white/10 bg-[#101827] px-4 py-3.5 text-left text-white shadow-[0_18px_45px_rgba(2,6,23,0.42)]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={`flex gap-3 ${hasSubheadline ? 'items-start' : 'items-center'}`}>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${tone.iconShellClassName}`}>
          <Icon className={`h-4.5 w-4.5 ${tone.iconClassName}`} />
        </div>
        <div className={`min-w-0 flex-1 ${hasSubheadline ? '' : 'flex min-h-[36px] items-center'}`}>
          <div>
          <div className="text-sm font-semibold text-white">{sanitizeHeadline(toast.headline)}</div>
          {toast.subheadline && (
            <div className="mt-1 text-sm text-slate-300">{toast.subheadline}</div>
          )}
          {toast.action && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAction}
              className="mt-2 text-xs font-semibold text-indigo-300 transition-colors hover:text-indigo-200"
            >
              {toast.action.label}
            </Button>
          )}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss notification"
          className="shrink-0 rounded-md p-1 text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-200"
        >
          <CloseIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const ToastViewport = ({ pinnedToast, toasts, onDismiss }) => {
  if (!pinnedToast && !toasts.length) return null;
  return (
    <div
      className="fixed bottom-20 left-4 right-4 flex flex-col-reverse items-stretch gap-3 md:bottom-6 md:left-6 md:right-auto md:w-full md:max-w-[360px] md:items-start"
      style={{ zIndex: Z.ERROR_TOAST }}
    >
      {pinnedToast ? (
        <div key={pinnedToast.id} className="w-full">
          {pinnedToast.node}
        </div>
      ) : null}
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const location = useLocation();
  const suppress = useMemo(() => shouldSuppressToasts(location.pathname), [location.pathname]);
  const [toasts, setToasts] = useState([]);
  const [pinnedToast, setPinnedToastState] = useState(null);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const pushToast = useCallback((toast) => {
    if (suppress) return;
    const durationMs = toast.durationMs || (toast.type === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS);
    const nextToast = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      headline: sanitizeHeadline(toast.headline),
      subheadline: toast.subheadline,
      type: toast.type || 'info',
      action: toast.action || null,
      durationMs
    };
    setToasts(prev => [nextToast, ...prev].slice(0, MAX_TOASTS));
  }, [suppress]);

  const setPinnedToast = useCallback((toast) => {
    if (!toast?.id || !toast?.node) return;
    setPinnedToastState({ id: toast.id, node: toast.node });
  }, []);

  const clearPinnedToast = useCallback((id) => {
    setPinnedToastState((current) => {
      if (!current) return null;
      if (id && current.id !== id) return current;
      return null;
    });
  }, []);

  const value = useMemo(
    () => ({
      pushToast,
      removeToast,
      toasts,
      pinnedToast,
      setPinnedToast,
      clearPinnedToast,
    }),
    [clearPinnedToast, pinnedToast, pushToast, removeToast, setPinnedToast, toasts]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport pinnedToast={pinnedToast} toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
